import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as paymentSelectors from '@makemydeal/dr-dash-store/selectors/payment';
import { CALC_ENDPOINTS } from '@makemydeal/dr-dash-store/constants';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import type { StateTree } from '@makemydeal/dr-dash-types';
import type { OfferType, ResidualInfo } from '@makemydeal/dr-platform-types';
import { LEASE, FINANCE, BALLOON } from '@makemydeal/dr-platform-types';

interface UPQRequiredFieldValidation {
    field: 'sellRate' | 'residual' | 'balloonPayment';
    isMissing: boolean;
}

interface UPQValidationResult {
    missingFields: UPQRequiredFieldValidation[];
    hasMissingRequiredFields: boolean;
    lastPaymentMissingUPQFields: boolean;
    isPersistentManualResidualInput: boolean;
}

const isMissingValue = (value: number | undefined): boolean => {
    return value === undefined || value === null;
};

const validateLeaseFields = (
    sellRateOverride: number | undefined,
    residualFields: ResidualInfo | undefined
): UPQRequiredFieldValidation[] => {
    const missingFields: UPQRequiredFieldValidation[] = [];

    // hasMissingResidual is true only when ALL 4 residual fields are empty (or the slice itself is absent)
    const hasMissingResidual = offerReduxSelectors.isLeaseResidualMissing(residualFields);

    if (hasMissingResidual) {
        missingFields.push({ field: 'residual', isMissing: true });
    }

    if (isMissingValue(sellRateOverride)) {
        missingFields.push({ field: 'sellRate', isMissing: true });
    }

    return missingFields;
};

const validateFinanceFields = (sellRateOverride: number | undefined): UPQRequiredFieldValidation[] => {
    const missingFields: UPQRequiredFieldValidation[] = [];

    if (isMissingValue(sellRateOverride)) {
        missingFields.push({ field: 'sellRate', isMissing: true });
    }

    return missingFields;
};

const validateBalloonFields = (
    sellRateOverride: number | undefined,
    balloonPaymentAmount: number | undefined
): UPQRequiredFieldValidation[] => {
    const missingFields: UPQRequiredFieldValidation[] = [];

    if (isMissingValue(balloonPaymentAmount)) {
        missingFields.push({ field: 'balloonPayment', isMissing: true });
    }

    if (isMissingValue(sellRateOverride)) {
        missingFields.push({ field: 'sellRate', isMissing: true });
    }

    return missingFields;
};

const isUPQCalculationEngine = (
    offerType: OfferType,
    financeCalcEndpoint: string | undefined,
    leaseCalcEndpoint: string | undefined,
    balloonCalcEndpoint: string | undefined
): boolean => {
    const endpoint = offerType === LEASE ? leaseCalcEndpoint : offerType === BALLOON ? balloonCalcEndpoint : financeCalcEndpoint;
    return endpoint === CALC_ENDPOINTS.userProgramQuotes;
};

// Module-scope selector arrows: stable identity for memoization and for identity-based test mocks.
const selectFinanceCalcEndpoint = (state: StateTree) => offerReduxSelectors.getCalculationEndpointByOfferType(state, FINANCE);
const selectLeaseCalcEndpoint = (state: StateTree) => offerReduxSelectors.getCalculationEndpointByOfferType(state, LEASE);
const selectBalloonCalcEndpoint = (state: StateTree) => offerReduxSelectors.getCalculationEndpointByOfferType(state, BALLOON);
// Override-only by design: UPQ preloads off explicit user input; program-derived/fallback values do not trigger it.
const selectFinanceSellRateOverride = (state: StateTree) => offerReduxSelectors.getSellRateOverride(state, false, FINANCE);
const selectLeaseSellRateOverride = (state: StateTree) => offerReduxSelectors.getSellRateOverride(state, false, LEASE);
const selectBalloonSellRateOverride = (state: StateTree) => offerReduxSelectors.getSellRateOverride(state, false, BALLOON);
const selectBalloonPaymentAmountOverride = (state: StateTree) =>
    offerReduxSelectors.getOfferDetails(state, BALLOON)?.balloonPaymentAmountOverride;

/**
 * Custom hook to validate required fields for UPQ calculation engine.
 * Returns missing field info so components can highlight inputs when a PQ preloading call fails.
 *
 * For LEASE: Requires sell rate + at least one residual (base or adjusted)
 * For FINANCE: Requires only sell rate
 * For BALLOON: Requires sell rate + balloon payment
 *
 * @param offerType - The offer type to validate (FINANCE, LEASE, BALLOON)
 * @returns Object containing missing fields list, validation flag, and PQ preloading failure flag
 */
export const useUPQRequiredFieldsValidation = (offerType: OfferType): UPQValidationResult => {
    const lastPaymentMissingUPQFields = useSelector(offerReduxSelectors.getLastPaymentMissingUPQFields);
    const isCalculating = useSelector(paymentSelectors.isCalculatingPayment);
    const isLeaseResidualTreatmentEnabled = useSelector(featureToggleSelectors.isLeaseResidualTreatmentEnabled);
    const financeCalcEndpoint = useSelector(selectFinanceCalcEndpoint);
    const leaseCalcEndpoint = useSelector(selectLeaseCalcEndpoint);
    const balloonCalcEndpoint = useSelector(selectBalloonCalcEndpoint);
    const financeSellRateOverride = useSelector(selectFinanceSellRateOverride);
    const leaseSellRateOverride = useSelector(selectLeaseSellRateOverride);
    const balloonSellRateOverride = useSelector(selectBalloonSellRateOverride);
    const residualFields = useSelector(offerReduxSelectors.getResidualInfoOverride);
    const balloonPaymentAmount = useSelector(selectBalloonPaymentAmountOverride);

    return useMemo(() => {
        if (
            ![FINANCE, LEASE, BALLOON].includes(offerType) ||
            !isUPQCalculationEngine(offerType, financeCalcEndpoint, leaseCalcEndpoint, balloonCalcEndpoint)
        ) {
            return {
                missingFields: [],
                hasMissingRequiredFields: false,
                lastPaymentMissingUPQFields: false,
                isPersistentManualResidualInput: false
            };
        }

        let missingFields: UPQRequiredFieldValidation[] = [];

        if (offerType === LEASE) {
            missingFields = validateLeaseFields(leaseSellRateOverride, residualFields);
        } else if (offerType === BALLOON) {
            missingFields = validateBalloonFields(balloonSellRateOverride, balloonPaymentAmount);
        } else {
            missingFields = validateFinanceFields(financeSellRateOverride);
        }

        const isPersistentManualResidualInput =
            isLeaseResidualTreatmentEnabled && offerType === LEASE && missingFields.some((f) => f.field === 'residual');

        return {
            missingFields,
            hasMissingRequiredFields: missingFields.length > 0,
            lastPaymentMissingUPQFields: missingFields.length > 0 && lastPaymentMissingUPQFields && !isCalculating,
            isPersistentManualResidualInput
        };
    }, [
        lastPaymentMissingUPQFields,
        isCalculating,
        isLeaseResidualTreatmentEnabled,
        offerType,
        financeCalcEndpoint,
        leaseCalcEndpoint,
        balloonCalcEndpoint,
        financeSellRateOverride,
        leaseSellRateOverride,
        balloonSellRateOverride,
        residualFields,
        balloonPaymentAmount
    ]);
};
